import React, { useEffect, useState } from "react";
import {
    Container,
    Paper,
    Typography,
    Grid,
    Box,
    Button,
    CircularProgress,
    Alert,
    TextField,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { VCatUserResponse, VCatUserUpdate } from "../../types/models";
import { getDetailedUserInfo, updateUser } from "../../services/User/service";
import { getBiometricData, updateBiometricData } from "../../services/BiometricData/service";
import { BiometricDataResponse, BiometricDataUpdate } from "../../types/models/biometricData";

const UserInfo: React.FC = () => {
    const [userInfo, setUserInfo] = useState<VCatUserResponse | null>(null);
    const [editedFields, setEditedFields] = useState<VCatUserUpdate>({});
    const [editingField, setEditingField] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [biometricData, setBiometricData] = useState<BiometricDataResponse | null>(null);

    const fetchUserInfo = async () => {
        setLoading(true);
        try {
            const userData = await getDetailedUserInfo();
            const bioData = await getBiometricData();

            setUserInfo(userData);
            setBiometricData(bioData);
            setError(null);
        } catch (err) {
            setError((err as Error).message || "Failed to fetch user information");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchUserInfo();
    }, []);

    const handleEdit = (field: keyof VCatUserUpdate) => {
        setEditingField(field);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setEditingField(null);
        setEditedFields({});
        setIsEditing(false);
    };

    const handleFieldChange = (field: keyof VCatUserUpdate, value: any) => {
        setEditedFields((prev) => ({ ...prev, [field]: value }));
        setIsEditing(true);
    };

    const handleUpdate = async () => {
        if (!userInfo) return;

        try {
            if (Object.keys(editedFields).length > 0) {
                await updateUser(editedFields);
            }

            if (biometricData && (editedFields.dominant_eye || editedFields.dominant_hand)) {
                await updateBiometricData({
                    dominant_eye: editedFields.dominant_eye,
                    dominant_hand: editedFields.dominant_hand,
                });
            }

            await fetchUserInfo();
            setEditedFields({});
            setEditingField(null);
            setIsEditing(false);
        } catch (err) {
            setError((err as Error).message || "Failed to update user information");
        }
    };


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }
    const needsSelection =
        biometricData?.dominant_eye === "UNKNOWN" || biometricData?.dominant_hand === "UNKNOWN";


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm">
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    if (!userInfo) {
        return (
            <Container maxWidth="sm">
                <Typography variant="h6" align="center">
                    No user information available.
                </Typography>
            </Container>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container
                maxWidth="lg"
                disableGutters
                sx={{
                    mt: 4,
                    mb: 10,
                    pl: 0,
                    ml: 0,
                }}
            >
                {needsSelection && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Please select your <strong>Dominant Eye</strong> and <strong>Dominant Hand</strong> to continue.
                    </Alert>
                )}
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ color: "#FFFFFF" }}
                >
                    Personal Information
                </Typography>

                <Paper
                    sx={{
                        p: 4,
                        mb: 4,
                        textAlign: "left",
                        width: "100%",
                        backgroundColor: "#071019",
                        border: "1px solid #394047",
                        borderRadius: "5px",            
                    }}
                >
                    <Grid container spacing={2}>
                        {/* Non-editable Fields: Username and Email */}
                        {[
                            { label: "Username", value: userInfo.username },
                            { label: "Email", value: userInfo.email },
                        ].map(({ label, value }) => (
                            <Grid item xs={12} sm={6} key={label}>
                                <Box display="flex" alignItems="center">
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flexGrow: 1,
                                            color: "#B5B7BA"
                                        }}
                                    >
                                        <strong>{label}:</strong>{" "}
                                        <Typography
                                            component="span"
                                            sx={{ color: "#FFFFFF" }}
                                        >
                                            {value}
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}

                        {/* Editable Fields */}
                        {[
                            { label: "First Name", field: "first_name", value: userInfo.first_name },
                            { label: "Last Name", field: "last_name", value: userInfo.last_name },
                            { label: "Phone", field: "phone", value: userInfo.phone },
                            {
                                label: "Date of Birth",
                                field: "date_of_birth",
                                value: userInfo.date_of_birth ? new Date(userInfo.date_of_birth) : null,
                                isDate: true,
                            },
                            { label: "Password", field: "password", value: "******" },
                        ].map(({ label, field, value, isDate }) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <Box display="flex" alignItems="center">
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            flexGrow: 1,
                                            color: "#B5B7BA",
                                        }}
                                    >
                                        <strong>{label}:</strong>{" "}
                                        {editingField === field ? (
                                            field === "password" ? (
                                                <TextField
                                                    fullWidth
                                                    type={showPassword ? "text" : "password"}
                                                    value={editedFields[field as keyof VCatUserUpdate] ?? ""}
                                                    onChange={(e) =>
                                                        handleFieldChange(field as keyof VCatUserUpdate, e.target.value)
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        ml: 2,
                                                        width: "200px",
                                                        "& .MuiInputBase-input": { color: "#FFFFFF" },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": { borderColor: "#B5B7BA" },
                                                            "&:hover fieldset": { borderColor: "#64FFDA" },
                                                            "&.Mui-focused fieldset": { borderColor: "#64FFDA" },
                                                        },
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            ) : isDate ? (
                                                <DatePicker
                                                    value={
                                                        editedFields[field as keyof VCatUserUpdate]
                                                            ? new Date(editedFields[field as keyof VCatUserUpdate] as string)
                                                            : value
                                                    }
                                                    onChange={(newValue) =>
                                                        handleFieldChange(
                                                            field as keyof VCatUserUpdate,
                                                            newValue ? newValue.toISOString() : ""
                                                        )
                                                    }
                                                    slotProps={{
                                                        textField: {
                                                            size: "small",
                                                            sx: {
                                                                ml: 2,
                                                                "& .MuiInputBase-input": { color: "#FFFFFF" },
                                                                "& .MuiOutlinedInput-root": {
                                                                    "& fieldset": { borderColor: "#B5B7BA" },
                                                                    "&:hover fieldset": { borderColor: "#64FFDA" },
                                                                    "&.Mui-focused fieldset": { borderColor: "#64FFDA" },
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    value={editedFields[field as keyof VCatUserUpdate] ?? value}
                                                    onChange={(e) =>
                                                        handleFieldChange(field as keyof VCatUserUpdate, e.target.value)
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        ml: 2,
                                                        width: "200px",
                                                        "& .MuiInputBase-input": { color: "#FFFFFF" },
                                                        "& .MuiOutlinedInput-root": {
                                                            "& fieldset": { borderColor: "#B5B7BA" },
                                                            "&:hover fieldset": { borderColor: "#64FFDA" },
                                                            "&.Mui-focused fieldset": { borderColor: "#64FFDA" },
                                                        },
                                                    }}
                                                />
                                            )
                                        ) : (
                                            <Typography
                                                component="span"
                                                sx={{ color: "#FFFFFF" }}
                                            >
                                                {value instanceof Date ? value.toLocaleDateString() : value}
                                            </Typography>
                                        )}
                                    </Typography>
                                    {editingField === field ? (
                                        <IconButton size="small" onClick={handleCancelEdit}>
                                            <CloseIcon sx={{ color: "red" }} />
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(field as keyof VCatUserUpdate)}
                                        >
                                            <EditIcon sx={{ color: "#64FFDA" }} />
                                        </IconButton>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>






                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ color: "#FFFFFF" }}
                >
                    Physiological Data
                </Typography>

                <Paper
                    sx={{
                        p: 3,
                        backgroundColor: "#071019",
                        border: "1px solid #394047",
                        borderRadius: "5px",            
                    }}
                >
                    <Grid container spacing={2}>
                        {[
                            { label: "Dominant Eye", field: "dominant_eye", value: biometricData?.dominant_eye },
                            { label: "Dominant Hand", field: "dominant_hand", value: biometricData?.dominant_hand },
                        ].map(({ label, field, value }) => (
                            <Grid item xs={12} sm={6} key={field}>
                                <Typography variant="body1" gutterBottom sx={{ color: "#B5B7BA" }}>
                                    <strong>{label}</strong>
                                </Typography>
                                <ToggleButtonGroup
                                    value={editedFields[field as keyof BiometricDataUpdate] ?? value}
                                    exclusive
                                    onChange={(_, newValue) =>
                                        handleFieldChange(field as keyof BiometricDataUpdate, newValue)
                                    }
                                    sx={{
                                        "& .MuiToggleButton-root": {
                                            color: "#A8B2D1",
                                            borderColor: "#A8B2D1",
                                            "&.Mui-selected": {
                                                backgroundColor: "#64FFDA",
                                                color: "#161B22",
                                            },
                                            "&:hover": {
                                                backgroundColor: "#64FFDA33",
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="LEFT">Left</ToggleButton>
                                    <ToggleButton value="RIGHT">Right</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                        ))
                        }
                    </Grid>
                </Paper>

                {isEditing && (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#64FFDA",
                            color: "#161B22",
                            "&:hover": { backgroundColor: "#4EDDC7" },
                            mt: 3,
                        }}
                        onClick={handleUpdate}
                    >
                        Update
                    </Button>
                )}

            </Container>
        </LocalizationProvider>
    );
};

export default UserInfo;
